
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Button, Table, Thead, Tbody, Tr, Th, Td } from '../../components/UI';
import { TrendingUp, CreditCard, ArrowUpRight, Zap } from '../../components/Icons';
import { usageData } from '../../data/usage';
import { useTranslation } from '../../contexts/LanguageContext';

const PlanSettings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = () => navigate('/dashboard');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const invoices = [
    { id: 'INV-2025-012', date: '01/01/2025', amount: '240.00 CHF', status: 'Paid' },
    { id: 'INV-2024-156', date: '12/01/2024', amount: '240.00 CHF', status: 'Paid' },
    { id: 'INV-2024-098', date: '11/01/2024', amount: '240.00 CHF', status: 'Paid' },
  ];

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const usagePercent = (usageData.txe.used / usageData.txe.total) * 100;

  return (
    <div className="p-6 space-y-8 max-w-[1200px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('plan_billing')} 
        breadcrumbs={[{ label: t('settings') }, { label: t('plan_billing') }]} 
        onBack={goBack}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        
        {/* Left Column: Current Plan Summary */}
        <div className="md:col-span-1 space-y-6">
          <Card className="p-8 border-brand-medium/20 shadow-md">
            <p className="text-[10px] font-black uppercase tracking-widest text-brand-medium mb-2">{t('current_plan')}</p>
            <h3 className="text-3xl font-black text-brand-dark dark:text-white tracking-tighter mb-4 italic">Enterprise</h3>
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300">
                <Zap size={16} className="text-brand-bright" />
                Unlimited Channels
              </div>
              <div className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300">
                <Zap size={16} className="text-brand-bright" />
                SLA 99.99% Guaranteed
              </div>
              <div className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300">
                <Zap size={16} className="text-brand-bright" />
                24/7 Premium Support
              </div>
            </div>
            <Button variant="primary" className="w-full">{t('upgrade_plan')}</Button>
          </Card>

          <Card className="p-8 bg-gray-50 dark:bg-[#1A1A1A] border-gray-100 dark:border-[#333]">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xs font-black uppercase tracking-widest text-brand-dark dark:text-white">{t('usage_tracker')}</h4>
              <TrendingUp size={16} className="text-brand-medium" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-2xl font-black text-brand-dark dark:text-white">{usageData.txe.used.toLocaleString()}</span>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">/ {usageData.txe.total.toLocaleString()} Transactions</span>
              </div>
              <div className="h-2 w-full bg-gray-200 dark:bg-[#333] rounded-full overflow-hidden">
                <div className="h-full bg-brand-medium transition-all duration-1000" style={{ width: `${usagePercent}%` }}></div>
              </div>
              <p className="text-[10px] font-bold text-gray-400 text-center uppercase tracking-widest pt-2">{t('next_reset')}: Feb 1st, 2025</p>
            </div>
          </Card>
        </div>

        {/* Right Column: Billing & Payment */}
        <div className="md:col-span-2 space-y-8">
          
          {/* Payment Method */}
          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <CreditCard className="text-brand-medium" size={20} />
              <h4 className="text-sm font-black uppercase tracking-widest text-brand-dark dark:text-white">{t('payment_method')}</h4>
            </div>
            <div className="flex items-center justify-between p-6 border border-gray-100 dark:border-[#333] rounded-xl bg-gray-50/50 dark:bg-[#1A1A1A]">
              <div className="flex items-center gap-6">
                <div className="w-14 h-10 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-blue-800 text-xs italic">VISA</div>
                <div>
                  <p className="text-sm font-black text-brand-dark dark:text-white uppercase tracking-widest">Visa ending in 4242</p>
                  <p className="text-xs text-gray-400 font-medium">Expires 12 / 2028</p>
                </div>
              </div>
              <Button size="sm" variant="ghost">Edit</Button>
            </div>
          </Card>

          {/* Billing History */}
          <div className="space-y-4">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 ml-4">{t('billing_history')}</h4>
            <Card>
              <Table>
                <Thead>
                  <Tr isHeader>
                    <Th className="align-top">
                      <div className="flex flex-col gap-2 pb-2">
                        <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('invoice_id')}</span>
                        <input 
                          className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                          placeholder="Filter..."
                          onChange={(e) => handleColumnFilterChange('id', e.target.value)}
                        />
                      </div>
                    </Th>
                    <Th className="align-top">
                      <div className="flex flex-col gap-2 pb-2">
                        <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('date')}</span>
                        <input 
                          type="date"
                          className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                          onChange={(e) => handleColumnFilterChange('date', e.target.value)}
                        />
                      </div>
                    </Th>
                    <Th className="align-top">
                      <div className="flex flex-col gap-2 pb-2">
                        <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('amount')}</span>
                        <input 
                          className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                          placeholder="Filter..."
                          onChange={(e) => handleColumnFilterChange('amount', e.target.value)}
                        />
                      </div>
                    </Th>
                    <Th className="align-top">
                      <div className="flex flex-col gap-2 pb-2">
                        <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('status')}</span>
                        <select 
                          className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                          onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="Paid">Paid</option>
                          <option value="Pending">Pending</option>
                        </select>
                      </div>
                    </Th>
                    <Th className="text-right align-top">
                       <div className="flex flex-col gap-2 pb-2 items-end h-full">
                          <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('actions')}</span>
                          <div className="h-8"></div>
                       </div>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {invoices.map((inv) => (
                    <Tr key={inv.id}>
                      <Td className="font-medium text-[#161616] dark:text-white">{inv.id}</Td>
                      <Td className="text-gray-500 dark:text-gray-400">{inv.date}</Td>
                      <Td className="text-[#161616] dark:text-white">{inv.amount}</Td>
                      <Td>
                        <span className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-full text-[10px] font-bold uppercase tracking-widest border border-green-100 dark:border-green-900/30">
                          {inv.status}
                        </span>
                      </Td>
                      <Td className="text-right">
                        <button className="text-blue-500 hover:text-blue-700 transition-colors p-2">
                          <ArrowUpRight size={18} />
                        </button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanSettings;
